import { useEffect } from 'react';

import { APPLICANT_ATTRIBUTION_BOTH, REAL_ESTATE_USAGES } from 'cpool-constants';
import type { APPLICANT_ATTRIBUTION_BOTH_TYPE } from 'cpool-constants';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { useLeadDetailsFormContext } from '~/features/leadDetails/context/LeadDetailsFormContext';
import type { SchemaRealEstatesForm } from '~/features/leadDetails/schemas/realEstates.schema';

type UseRealEstateSelfOccupationParams = {
    currentApplicantAttribution: APPLICANT_ATTRIBUTION_BOTH_TYPE;
    onApplicantAttributionChange: (index: number, value: APPLICANT_ATTRIBUTION_BOTH_TYPE) => void;
    realEstateIndex: number;
};

function isSelfOccupiedUsage(usageType: SchemaRealEstatesForm['immobilien'][number]['nutzungsart']): boolean {
    return usageType === REAL_ESTATE_USAGES.EIGENGENUTZT || usageType === REAL_ESTATE_USAGES.EIGENGENUTZT_UND_VERMIETET;
}

export function useRealEstateSelfOccupation({
    currentApplicantAttribution,
    onApplicantAttributionChange,
    realEstateIndex,
}: UseRealEstateSelfOccupationParams) {
    const { t } = useTranslation();
    const {
        hasSelfOccupiedRealEstateApplicant1,
        hasSelfOccupiedRealEstateApplicant2,
        hasSelfOccupiedRealEstateShared,
        setHasSelfOccupiedRealEstateApplicant1,
        setHasSelfOccupiedRealEstateApplicant2,
        setHasSelfOccupiedRealEstateShared,
    } = useLeadDetailsFormContext();
    const { control, getValues } = useFormContext<SchemaRealEstatesForm>();
    const { field: usageTypeField } = useController({
        control,
        name: `immobilien.${realEstateIndex}.nutzungsart`,
    });

    const setSelfOccupation = (isSelfOccupied: boolean) => {
        switch (currentApplicantAttribution) {
            case APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_1:
                if (hasSelfOccupiedRealEstateApplicant1 !== isSelfOccupied) {
                    setHasSelfOccupiedRealEstateApplicant1(isSelfOccupied);
                }
                break;
            case APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_2:
                if (hasSelfOccupiedRealEstateApplicant2 !== isSelfOccupied) {
                    setHasSelfOccupiedRealEstateApplicant2(isSelfOccupied);
                }
                break;
            case APPLICANT_ATTRIBUTION_BOTH.BEIDE:
                if (
                    hasSelfOccupiedRealEstateShared !== isSelfOccupied ||
                    hasSelfOccupiedRealEstateApplicant1 !== isSelfOccupied ||
                    hasSelfOccupiedRealEstateApplicant2 !== isSelfOccupied
                ) {
                    setHasSelfOccupiedRealEstateShared(isSelfOccupied);
                }
                break;
            default:
                break;
        }
    };

    const onUsageTypeChange = (newUsageType: keyof typeof REAL_ESTATE_USAGES) => {
        const realEstates = getValues('immobilien');
        const hasNoConflictingSelfOccupiedRealEstate = realEstates.every((realEstate) => {
            if (!isSelfOccupiedUsage(realEstate.nutzungsart) || realEstate.id === realEstates[realEstateIndex].id)
                return true;

            if (currentApplicantAttribution === APPLICANT_ATTRIBUTION_BOTH.BEIDE) return false;

            return (
                realEstate.gehoertZuAntragsteller !== currentApplicantAttribution &&
                realEstate.gehoertZuAntragsteller !== APPLICANT_ATTRIBUTION_BOTH.BEIDE
            );
        });
        const isSelfOccupied = isSelfOccupiedUsage(newUsageType);

        if (!hasNoConflictingSelfOccupiedRealEstate && isSelfOccupied) {
            toast.error(t('REAL_ESTATE.TOASTS.ERROR_CHANGING_SELF_OCCUPATION'));
            return;
        }

        const wasSelfOccupied = isSelfOccupiedUsage(usageTypeField.value);

        if (!wasSelfOccupied && !isSelfOccupied && !hasNoConflictingSelfOccupiedRealEstate) return;
        if (isSelfOccupied && !wasSelfOccupied) setSelfOccupation(true);
        else if (!isSelfOccupied) setSelfOccupation(false);

        usageTypeField.onChange(newUsageType);
    };

    const changeApplicantAttribution = (newApplicantAttribution: APPLICANT_ATTRIBUTION_BOTH_TYPE) => {
        if (currentApplicantAttribution === newApplicantAttribution) return false;
        if (isSelfOccupiedUsage(usageTypeField.value)) {
            switch (newApplicantAttribution) {
                case APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_1:
                    if (
                        currentApplicantAttribution !== APPLICANT_ATTRIBUTION_BOTH.BEIDE &&
                        hasSelfOccupiedRealEstateApplicant1
                    ) {
                        toast.error(t('REAL_ESTATE.TOASTS.ERROR_CHANGING_SELF_OCCUPATION'));
                        return false;
                    }
                    if (hasSelfOccupiedRealEstateShared) setHasSelfOccupiedRealEstateShared(false);
                    if (hasSelfOccupiedRealEstateApplicant2) setHasSelfOccupiedRealEstateApplicant2(false);
                    break;
                case APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_2:
                    if (
                        currentApplicantAttribution !== APPLICANT_ATTRIBUTION_BOTH.BEIDE &&
                        hasSelfOccupiedRealEstateApplicant2
                    ) {
                        toast.error(t('REAL_ESTATE.TOASTS.ERROR_CHANGING_SELF_OCCUPATION'));
                        return false;
                    }
                    if (hasSelfOccupiedRealEstateShared) setHasSelfOccupiedRealEstateShared(false);
                    if (hasSelfOccupiedRealEstateApplicant1) setHasSelfOccupiedRealEstateApplicant1(false);
                    break;
                case APPLICANT_ATTRIBUTION_BOTH.BEIDE:
                    if (
                        (currentApplicantAttribution === APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_1 &&
                            hasSelfOccupiedRealEstateApplicant2) ||
                        (currentApplicantAttribution === APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_2 &&
                            hasSelfOccupiedRealEstateApplicant1)
                    ) {
                        toast.error(t('REAL_ESTATE.TOASTS.ERROR_CHANGING_SELF_OCCUPATION'));
                        return false;
                    }
                    setHasSelfOccupiedRealEstateShared(true);
                    if (hasSelfOccupiedRealEstateApplicant1) setHasSelfOccupiedRealEstateApplicant1(false);
                    if (hasSelfOccupiedRealEstateApplicant2) setHasSelfOccupiedRealEstateApplicant2(false);
                    break;
                default:
                    break;
            }
        }
        onApplicantAttributionChange(realEstateIndex, newApplicantAttribution);
        return true;
    };

    useEffect(() => {
        if (
            (currentApplicantAttribution === APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_1 &&
                !hasSelfOccupiedRealEstateApplicant1) ||
            (currentApplicantAttribution === APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_2 &&
                !hasSelfOccupiedRealEstateApplicant2) ||
            (currentApplicantAttribution === APPLICANT_ATTRIBUTION_BOTH.BEIDE &&
                !hasSelfOccupiedRealEstateShared &&
                !hasSelfOccupiedRealEstateApplicant1 &&
                !hasSelfOccupiedRealEstateApplicant2)
        ) {
            setSelfOccupation(isSelfOccupiedUsage(usageTypeField.value));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        changeApplicantAttribution,
        onUsageTypeChange,
        usageType: usageTypeField.value,
    };
}
